import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  CountrySelect,
  CustomSelect,
  DateTimePicker,
} from '@/components';
import {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'react-phone-number-input/max';
import toast from 'react-hot-toast';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { getCRMTags, getCRMCustomFields, createCRMTag } from '../api';
import { getCompanyEmployees } from '../../api';
import { useEnterSubmit, useMobileScrollLock } from '@/common/hooks';
import { formatName } from '@/common/utils';
import { parsePhoneForForm, buildPhonePayload } from '../utils/phoneUtils';

export function CRMContactForm({
  companyId,
  initialContact,
  onSubmit,
  isPending,
  apiErrors,
  entity = 'contact',
}) {
  const entityLabel = entity === 'lead' ? 'Lead' : 'Contact';
  const [countryCode, setCountryCode] = useState('IN');
  const [phoneInput, setPhoneInput] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [tags, setTags] = useState(() => {
    const tagsData = initialContact?.tags;
    if (!tagsData) return '';
    if (Array.isArray(tagsData)) {
      return tagsData
        .map((t) => (typeof t === 'object' ? String(t.name || '') : String(t)))
        .join(', ');
    }
    return typeof tagsData === 'string' ? tagsData : String(tagsData);
  });
  const [consentStatus, setConsentStatus] = useState('unknown');
  const [customFieldsValues, setCustomFieldsValues] = useState({});
  const [metadata, setMetadata] = useState('');
  const [errors, setErrors] = useState({});
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [debouncedTagSearch, setDebouncedTagSearch] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('0');
  // name -> id for tags created in this session, resolved as soon as the
  // create-tag POST responds — doesn't depend on the crm-tags list query
  // having refetched yet, so submit can always resolve a real tag_id for
  // a tag the user just created.
  const [createdTagIds, setCreatedTagIds] = useState({});

  // New states for Assign User and Source
  const [assigneeId, setAssigneeId] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [debouncedAssigneeSearch, setDebouncedAssigneeSearch] = useState('');
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);

  useMobileScrollLock(isTagsDropdownOpen || isAssigneeDropdownOpen);
  const [sourceType, setSourceType] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [isPrimarySource, setIsPrimarySource] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAssigneeSearch(assigneeSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [assigneeSearchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTagSearch(tagSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [tagSearchQuery]);

  useEffect(() => {
    if (apiErrors && Object.keys(apiErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...apiErrors }));
    }
  }, [apiErrors]);

  const queryClient = useQueryClient();

  const handleCreateTagMutation = useMutation({
    mutationFn: createCRMTag,
    onSuccess: (createdTag) => {
      setCreatedTagIds((prev) => ({
        ...prev,
        [createdTag.name]: createdTag.id,
      }));
      // Seed the cache immediately so selectedTagIds (below) can resolve
      // this tag's id right away — invalidateQueries alone refetches
      // async, so submitting the form right after creating a tag would
      // otherwise race the refetch and silently drop the new tag from
      // tag_ids (it just wasn't in tagsList yet).
      queryClient.setQueriesData(
        { queryKey: ['crm-tags', companyId], exact: false },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page, idx) => {
              if (idx !== 0) return page;
              return Array.isArray(page)
                ? [createdTag, ...page]
                : { ...page, results: [createdTag, ...(page.results || [])] };
            }),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: ['crm-tags', companyId] });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-tags', companyId],
      });
    },
    onError: () => {
      toast.error('Failed to create tag.');
    },
  });

  const generateRandomColor = () => {
    const colors = [
      '#3b82f6',
      '#f97316',
      '#22c55e',
      '#a855f7',
      '#ef4444',
      '#eab308',
      '#ec4899',
      '#6b7280',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleCreateNewTag = (tagName) => {
    const currentTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (!currentTags.includes(tagName)) {
      setTags([...currentTags, tagName].join(', '));
    }
    setTagSearchQuery('');

    handleCreateTagMutation.mutate({
      companyId,
      payload: { name: tagName, color: generateRandomColor() },
    });
  };

  const {
    data: assigneeData,
    isLoading: isAssigneesLoading,
    fetchNextPage: fetchNextAssigneePage,
    hasNextPage: hasNextAssigneePage,
    isFetchingNextPage: isFetchingNextAssigneePage,
  } = useInfiniteQuery({
    queryKey: ['companyEmployees-search', companyId, debouncedAssigneeSearch],
    queryFn: ({ pageParam = 1 }) => {
      const params = debouncedAssigneeSearch
        ? { search: debouncedAssigneeSearch, page: pageParam }
        : { page: pageParam };
      return getCompanyEmployees(companyId, params);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          return url.searchParams.get('page') || undefined;
        } catch {
          const match = lastPage.next.match(/[?&]page=(\d+)/);
          return match ? match[1] : undefined;
        }
      }
      return undefined;
    },
    enabled: !!companyId,
  });

  const {
    data: tagsData,
    isLoading: isTagsLoading,
    fetchNextPage: fetchNextTagPage,
    hasNextPage: hasNextTagPage,
    isFetchingNextPage: isFetchingNextTagPage,
  } = useInfiniteQuery({
    queryKey: ['crm-tags', companyId, debouncedTagSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const params = debouncedTagSearch
        ? { search: debouncedTagSearch, page: pageParam }
        : { page: pageParam };
      return getCRMTags(companyId, params);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.next) {
        try {
          const url = new URL(lastPage.next);
          return url.searchParams.get('page') || undefined;
        } catch {
          const match = lastPage.next.match(/[?&]page=(\d+)/);
          return match ? match[1] : undefined;
        }
      }
      return undefined;
    },
    enabled: !!companyId,
  });

  const assigneeList =
    assigneeData?.pages?.flatMap((page) =>
      Array.isArray(page) ? page : page?.results || [],
    ) || [];

  const tagsList =
    tagsData?.pages?.flatMap((page) =>
      Array.isArray(page) ? page : page?.results || [],
    ) || [];

  const formTagsInputRef = useRef(null);
  const formTagsDropdownRef = useRef(null);
  const [tagsDropdownStyle, setTagsDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });

  const formAssigneeInputRef = useRef(null);
  const formAssigneeDropdownRef = useRef(null);
  const [assigneeDropdownStyle, setAssigneeDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (isTagsDropdownOpen && formTagsInputRef.current) {
        const rect = formTagsInputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        let newPlacement = 'bottom';
        if (spaceBelow < 250 && spaceAbove > spaceBelow) {
          newPlacement = 'top';
        }
        setTagsDropdownStyle({
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          ...(newPlacement === 'bottom'
            ? { top: rect.bottom + 4 }
            : { bottom: window.innerHeight - rect.top + 4 }),
        });
      }
    };

    updatePosition();
    if (isTagsDropdownOpen) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isTagsDropdownOpen]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (isAssigneeDropdownOpen && formAssigneeInputRef.current) {
        const rect = formAssigneeInputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        let newPlacement = 'bottom';
        if (spaceBelow < 250 && spaceAbove > spaceBelow) {
          newPlacement = 'top';
        }
        setAssigneeDropdownStyle({
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          ...(newPlacement === 'bottom'
            ? { top: rect.bottom + 4 }
            : { bottom: window.innerHeight - rect.top + 4 }),
        });
      }
    };

    updatePosition();
    if (isAssigneeDropdownOpen) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isAssigneeDropdownOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isTagsDropdownOpen &&
        formTagsInputRef.current &&
        !formTagsInputRef.current.contains(event.target) &&
        (!formTagsDropdownRef.current ||
          !formTagsDropdownRef.current.contains(event.target))
      ) {
        setIsTagsDropdownOpen(false);
      }
      if (
        isAssigneeDropdownOpen &&
        formAssigneeInputRef.current &&
        !formAssigneeInputRef.current.contains(event.target) &&
        (!formAssigneeDropdownRef.current ||
          !formAssigneeDropdownRef.current.contains(event.target))
      ) {
        setIsAssigneeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isTagsDropdownOpen, isAssigneeDropdownOpen]);

  const handleTagsScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 30) {
      if (hasNextTagPage && !isFetchingNextTagPage) {
        fetchNextTagPage();
      }
    }
  };

  const handleAssigneeScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 30) {
      if (hasNextAssigneePage && !isFetchingNextAssigneePage) {
        fetchNextAssigneePage();
      }
    }
  };

  const { data: customFieldsData } = useQuery({
    queryKey: ['crm-custom-fields', companyId],
    queryFn: async () => {
      const res = await getCRMCustomFields(companyId);
      return res?.results || res || [];
    },
  });

  useEffect(() => {
    if (initialContact) {
      const fullName = String(initialContact.name || '');
      const parts = fullName.trim().split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.length > 1 ? parts.slice(1).join(' ') : '');
      setEmail(initialContact.email || '');

      const tagsData = initialContact.tags || [];
      const tagsString = Array.isArray(tagsData)
        ? tagsData
            .map((t) =>
              typeof t === 'object' ? String(t.name || '') : String(t),
            )
            .join(', ')
        : typeof tagsData === 'string'
          ? tagsData
          : String(tagsData);
      setTags(tagsString);

      setConsentStatus(initialContact.consent_status || 'unknown');

      let parsedCustomFields = {};
      if (initialContact.custom_fields) {
        try {
          parsedCustomFields =
            typeof initialContact.custom_fields === 'string'
              ? JSON.parse(initialContact.custom_fields)
              : initialContact.custom_fields;
        } catch {
          // ignore
        }
      }
      setCustomFieldsValues(parsedCustomFields || {});

      const meta = initialContact.metadata;
      setMetadata(
        typeof meta === 'object' && meta !== null
          ? JSON.stringify(meta)
          : meta || '',
      );

      const parsedPhone = parsePhoneForForm(initialContact);
      setCountryCode(parsedPhone.countryCode);
      setPhoneInput(parsedPhone.phoneInput);

      setEstimatedValue(
        initialContact.estimated_value !== null &&
          initialContact.estimated_value !== undefined
          ? String(initialContact.estimated_value)
          : '0',
      );

      let empId =
        initialContact.assigned_to_employee_id ||
        initialContact.assigned_to_id ||
        initialContact.assignee_id ||
        initialContact.assignee?.id ||
        '';
      let empName =
        initialContact.assigned_to_employee_name ||
        initialContact.assigned_to_name ||
        initialContact.assignee?.name ||
        '';

      if (empName && empName.toLowerCase() === 'unassigned') {
        empName = '';
        empId = '';
      }

      setAssigneeId(empId);
      setAssigneeName(empName);
      setAssigneeSearchQuery(empName);
    } else {
      setCountryCode('IN');
      setPhoneInput('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setTags('');
      setTagSearchQuery('');
      setConsentStatus('unknown');
      setCustomFieldsValues({});
      setMetadata('');
      setErrors({});
      setEstimatedValue('0');

      setAssigneeId('');
      setAssigneeName('');
      setAssigneeSearchQuery('');
      setSourceType('');
      setCampaignId('');
      setCampaignName('');
      setUtmSource('');
      setUtmMedium('');
      setUtmCampaign('');
      setIsPrimarySource(true);
    }
  }, [initialContact]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    let newErrors = {};

    let cleanPhone = undefined;
    let cleanCountryCode = undefined;
    let cleanCountryIso = undefined;

    if (phoneInput) {
      const fullNumber = buildPhonePayload(countryCode, phoneInput);
      // For countries sharing a calling code (e.g. +1 for US/CA/AG/...),
      // validating the full "+..." string only checks it's valid for
      // *some* country on that code, not the one the user selected — so
      // when the user typed a national number (no leading +), validate it
      // against the selected country specifically.
      const isInternationalInput = phoneInput.trim().startsWith('+');
      const isValid = isInternationalInput
        ? isValidPhoneNumber(fullNumber)
        : isValidPhoneNumber(phoneInput.replace(/[^\d]/g, ''), countryCode);
      if (!isValid) {
        newErrors.phone = 'Please enter a valid phone number.';
      } else {
        const parsed = parsePhoneNumber(fullNumber);
        if (parsed) {
          cleanPhone = parsed.nationalNumber;
          cleanCountryCode = `+${parsed.countryCallingCode}`;
          // Prefer the user's explicit country selection over
          // libphonenumber's area-code guess — countries that share a
          // calling code (e.g. +1 for US/CA/AG/...) can't be disambiguated
          // from the number alone, so trust the dropdown the user picked.
          cleanCountryIso = countryCode || parsed.country;
        } else {
          // fallback
          cleanPhone = phoneInput.replace(/[^\d]/g, '');
        }
      }
    }

    const name = formatName([firstName, lastName].filter(Boolean).join(' '));

    if (!name) {
      newErrors.name = 'Name is required.';
    }

    if (customFieldsData) {
      customFieldsData.forEach((field) => {
        if (field.is_required && !customFieldsValues[field.slug]) {
          newErrors[`cf_${field.slug}`] = `${field.name} is required.`;
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form.');
      return;
    }

    const selectedTagsArray = tags
      ? tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : [];

    const selectedTagIds = selectedTagsArray
      .map((tagName) => {
        const tagObj = tagsList?.find((t) => t.name === tagName);
        return tagObj?.id ?? createdTagIds[tagName];
      })
      .filter((id) => id !== undefined);

    const fieldsArray = customFieldsData?.results || customFieldsData || [];
    const customFieldsMap = new Map(fieldsArray.map((f) => [f.slug, f]));
    const filteredCustomFields = {};
    Object.keys(customFieldsValues).forEach((key) => {
      if (customFieldsMap.has(key)) {
        const field = customFieldsMap.get(key);
        let value = customFieldsValues[key];

        if (
          field.field_type === 'number' &&
          value !== '' &&
          value !== null &&
          !isNaN(Number(value))
        ) {
          filteredCustomFields[key] = Number(value);
        } else {
          filteredCustomFields[key] = value;
        }
      }
    });

    const payload = {
      name,
      email,
      phone: cleanPhone || undefined,
      country_code: cleanCountryCode || undefined,
      country_iso: cleanCountryIso || undefined,
      tags: selectedTagsArray,
      tag_ids: selectedTagIds,
      consent_status: consentStatus,
      estimated_value:
        estimatedValue.trim() !== '' ? estimatedValue.trim() : null,
      custom_fields:
        Object.keys(filteredCustomFields).length > 0
          ? filteredCustomFields
          : undefined,
      metadata: metadata.trim(),
    };

    if (assigneeId) {
      payload.assigned_to_employee_id = assigneeId;
    }

    const hasSourceData = Boolean(
      sourceType.trim() ||
      campaignId.trim() ||
      campaignName.trim() ||
      utmSource.trim() ||
      utmMedium.trim() ||
      utmCampaign.trim(),
    );

    if (hasSourceData) {
      payload.source = {
        source_type: sourceType.trim() || 'Manual',
        campaign_id: campaignId.trim() || '',
        campaign_name: campaignName.trim() || '',
        utm_source: utmSource.trim() || '',
        utm_medium: utmMedium.trim() || '',
        utm_campaign: utmCampaign.trim() || '',
        is_primary: isPrimarySource,
      };
    }

    onSubmit(payload);
  };

  const formRef = useEnterSubmit({
    onSubmit: handleSubmit,
    isPending,
  });

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="flex flex-col h-full overflow-hidden"
      autoComplete="off"
    >
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 flex flex-col gap-5 bg-white">
        {/* Basic Info */}
        <div className="flex flex-col gap-3">
          <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
            Basic Information
          </h4>
          <div className="bg-[#F5F5F5] rounded-[12px] p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="firstName"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.name) setErrors({ ...errors, name: null });
                  }}
                  placeholder="Enter first name"
                  className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors.name ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  disabled={isPending}
                />
                {errors.name && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.name}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="lastName"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.last_name)
                      setErrors({ ...errors, last_name: null });
                  }}
                  placeholder="Enter last name"
                  className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors.last_name ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  disabled={isPending}
                />
                {errors.last_name && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.last_name}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="email"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: null });
                  }}
                  placeholder="Enter email address"
                  className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors.email ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  disabled={isPending}
                />
                {errors.email && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.email}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <label
                  htmlFor="phoneInput"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div
                  className={`flex items-center w-full h-11 rounded-xl bg-layer-1-page border transition-colors ${
                    errors.phone
                      ? 'border-red-500'
                      : 'border-border-default focus-within:border-primary'
                  }`}
                >
                  <CountrySelect
                    value={countryCode}
                    onChange={(code) => {
                      setCountryCode(code);
                    }}
                    disabled={isPending}
                    className="w-[102px] shrink-0 h-full"
                    buttonClassName="!rounded-none !border-0 !bg-transparent !px-2.5 !text-[14px]"
                  />
                  <span
                    aria-hidden="true"
                    className="w-px h-6 bg-[#D0D5D1] shrink-0"
                  />
                  <input
                    type="tel"
                    id="phoneInput"
                    value={phoneInput}
                    onChange={(e) => {
                      setPhoneInput(e.target.value.replace(/[^\d+]/g, ''));
                      if (errors.phone) setErrors({ ...errors, phone: null });
                    }}
                    placeholder="Enter phone number"
                    className="flex-grow min-w-0 w-full h-full px-3 bg-transparent border-0 text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none"
                    disabled={isPending}
                  />
                </div>
                {errors.phone && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.phone}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="estimatedValue"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Estimated Value
                </label>
                <input
                  type="text"
                  id="estimatedValue"
                  value={estimatedValue}
                  onChange={(e) => {
                    setEstimatedValue(e.target.value.replace(/[^0-9.]/g, ''));
                    if (errors.estimated_value)
                      setErrors({ ...errors, estimated_value: null });
                  }}
                  placeholder="e.g. 25000.50"
                  className={`w-full h-11 px-4 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors.estimated_value ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  disabled={isPending}
                />
                {errors.estimated_value && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.estimated_value}
                  </span>
                )}
              </div>

              {/* Metadata */}
              <div className="flex flex-col gap-2 mt-2 sm:col-span-3">
                <label
                  htmlFor="metadata"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Additional Data
                </label>
                <textarea
                  id="metadata"
                  value={metadata}
                  onChange={(e) => {
                    setMetadata(e.target.value);
                    if (errors.metadata)
                      setErrors({ ...errors, metadata: null });
                  }}
                  placeholder="Enter additional data"
                  rows={3}
                  className={`w-full px-4 py-3 rounded-xl bg-layer-1-page border text-text-primary text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors custom-scrollbar resize-y ${errors.metadata ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                  disabled={isPending}
                />
                {errors.metadata && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.metadata}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tags & Consent */}
        <div className="flex flex-col gap-3">
          <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
            Tags & Consent
          </h4>
          <div className="bg-[#F5F5F5] rounded-[12px] p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label className="text-[14px] font-medium text-text-secondary">
                  Tags
                </label>
                <div className="relative flex flex-col" ref={formTagsInputRef}>
                  <div
                    className={`relative w-full min-h-[44px] rounded-xl bg-[#FFFFFF] border transition-colors cursor-text ${
                      isTagsDropdownOpen
                        ? 'border-[#50AB3D]'
                        : 'border-border-default hover:border-[#50AB3D]'
                    }`}
                    onClick={() => {
                      if (!isPending) {
                        setIsTagsDropdownOpen(true);
                        document.getElementById('tagSearchInput')?.focus();
                      }
                    }}
                  >
                    <div className="flex flex-wrap items-center gap-1.5 py-2 pl-3 pr-10">
                      {tags
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean)
                        .map((tag, idx) => {
                          const tagColor = tagsList.find(
                            (t) => t.name === tag,
                          )?.color;
                          return (
                            <span
                              key={`selected-${tag}-${idx}`}
                              className={`inline-flex max-w-full items-center gap-1 rounded-[8px] px-2.5 py-1 text-[12px] font-medium ${!tagColor ? 'bg-[#EBF4EA] text-[#50AB3D]' : ''}`}
                              style={
                                tagColor
                                  ? {
                                      backgroundColor: `${tagColor}20`,
                                      color: tagColor,
                                    }
                                  : undefined
                              }
                            >
                              <span className="truncate">{tag}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const tagArr = tags
                                    .split(',')
                                    .map((t) => t.trim())
                                    .filter(Boolean)
                                    .filter((t) => t !== tag);
                                  setTags(tagArr.join(', '));
                                }}
                                className="shrink-0 hover:opacity-70 transition-opacity focus:outline-none leading-none"
                                style={
                                  tagColor ? { color: tagColor } : undefined
                                }
                                disabled={isPending}
                                aria-label={`Remove ${tag}`}
                              >
                                &times;
                              </button>
                            </span>
                          );
                        })}
                      <input
                        id="tagSearchInput"
                        type="text"
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && tagSearchQuery.trim()) {
                            e.preventDefault();
                            const newTag = tagSearchQuery.trim();
                            const tagArr = tags
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean);
                            if (!tagArr.includes(newTag)) {
                              handleCreateNewTag(newTag);
                            }
                          }
                          if (
                            e.key === 'Backspace' &&
                            !tagSearchQuery &&
                            tags.split(',').filter(Boolean).length > 0
                          ) {
                            const tagArr = tags
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean);
                            tagArr.pop();
                            setTags(tagArr.join(', '));
                          }
                        }}
                        onFocus={() => setIsTagsDropdownOpen(true)}
                        onBlur={() =>
                          setTimeout(() => setIsTagsDropdownOpen(false), 200)
                        }
                        placeholder={
                          !tags || tags.split(',').filter(Boolean).length === 0
                            ? 'Search and select tags...'
                            : 'Add more...'
                        }
                        className="flex-1 min-w-[100px] bg-transparent text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none py-1 cursor-text"
                        disabled={isPending}
                        autoComplete="off"
                      />
                    </div>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsTagsDropdownOpen((prev) => !prev);
                        if (!isTagsDropdownOpen) {
                          document.getElementById('tagSearchInput')?.focus();
                        }
                      }}
                      className="absolute right-0 top-0 bottom-0 px-3 flex items-center justify-center cursor-pointer text-[#50AB3D] focus:outline-none z-10"
                      aria-label="Toggle tags dropdown"
                    >
                      <svg
                        className={`w-4 h-4 flex-shrink-0 transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </button>
                  </div>
                  {isTagsDropdownOpen &&
                    createPortal(
                      <div
                        ref={formTagsDropdownRef}
                        style={{
                          ...tagsDropdownStyle,
                          overscrollBehavior: 'contain',
                        }}
                        data-portal-dropdown="true"
                        className="z-[99999] bg-[#FFFFFF] border border-border-default rounded-xl shadow-2xl max-h-[250px] overflow-y-auto custom-scrollbar py-1"
                        onScroll={handleTagsScroll}
                      >
                        {isTagsLoading ? (
                          <div className="p-3 text-center text-xs text-text-muted">
                            Loading tags...
                          </div>
                        ) : tagsList.filter(
                            (t) =>
                              !tags
                                .split(',')
                                .map((x) => x.trim())
                                .includes(t.name),
                          ).length > 0 ? (
                          <>
                            {tagsList
                              .filter(
                                (t) =>
                                  !tags
                                    .split(',')
                                    .map((x) => x.trim())
                                    .includes(t.name),
                              )
                              .map((tag) => (
                                <button
                                  key={tag.id}
                                  type="button"
                                  className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-[14px] font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const currentTags = tags
                                      .split(',')
                                      .map((t) => t.trim())
                                      .filter(Boolean);
                                    setTags(
                                      [...currentTags, tag.name].join(', '),
                                    );
                                    setTagSearchQuery('');
                                  }}
                                >
                                  <span className="truncate">{tag.name}</span>
                                </button>
                              ))}
                            {isFetchingNextTagPage && (
                              <div className="p-2 text-center text-xs text-text-muted">
                                Loading more...
                              </div>
                            )}
                          </>
                        ) : tagSearchQuery.trim() ? (
                          <button
                            type="button"
                            className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-[14px] font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newTag = tagSearchQuery.trim();
                              const currentTags = tags
                                .split(',')
                                .map((t) => t.trim())
                                .filter(Boolean);
                              if (!currentTags.includes(newTag)) {
                                handleCreateNewTag(newTag);
                              }
                            }}
                          >
                            <span className="truncate">
                              Create &quot;{tagSearchQuery.trim()}&quot;
                            </span>
                          </button>
                        ) : (
                          <div className="p-3 text-center text-xs text-text-muted">
                            No tags found
                          </div>
                        )}
                      </div>,
                      document.body,
                    )}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="consentStatus"
                  className="text-[14px] font-medium text-text-secondary"
                >
                  Consent Status
                </label>
                <CustomSelect
                  value={consentStatus}
                  onChange={(val) => {
                    setConsentStatus(val);
                    if (errors.consent_status)
                      setErrors({ ...errors, consent_status: null });
                  }}
                  options={[
                    { value: 'opt_in', label: 'Opt In' },
                    { value: 'opt_out', label: 'Opt Out' },
                    { value: 'unknown', label: 'Unknown' },
                  ]}
                  placeholder="Select Status"
                  disabled={isPending}
                  error={Boolean(errors.consent_status)}
                  className="w-full h-11"
                  textSizeClass="text-[14px] font-medium"
                />
                {errors.consent_status && (
                  <span className="text-red-500 text-xs font-medium">
                    {errors.consent_status}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assignment */}
        <div className="flex flex-col gap-3">
          <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
            Assign User
          </h4>
          <div className="bg-[#F5F5F5] rounded-[12px] p-4">
            <div className="flex flex-col gap-2 relative">
              <label
                htmlFor="assigneeSearch"
                className="text-[14px] font-medium text-text-secondary"
              >
                Assign User
              </label>
              <div
                className="relative flex items-center w-full"
                ref={formAssigneeInputRef}
              >
                <input
                  type="text"
                  id="assigneeSearch"
                  value={
                    isAssigneeDropdownOpen ? assigneeSearchQuery : assigneeName
                  }
                  onChange={(e) => {
                    setAssigneeSearchQuery(e.target.value);
                    setAssigneeName(e.target.value);
                    setAssigneeId('');
                    setIsAssigneeDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setIsAssigneeDropdownOpen(true);
                    setAssigneeSearchQuery(assigneeName);
                  }}
                  onBlur={() =>
                    setTimeout(() => setIsAssigneeDropdownOpen(false), 200)
                  }
                  placeholder="Search and select user..."
                  className={`w-full h-11 px-4 pr-10 rounded-xl bg-[#FFFFFF] border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors cursor-text ${
                    isAssigneeDropdownOpen
                      ? 'border-[#50AB3D]'
                      : 'border-border-default focus:border-[#50AB3D]'
                  }`}
                  disabled={isPending}
                />
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAssigneeDropdownOpen((prev) => !prev);
                    if (!isAssigneeDropdownOpen) {
                      document.getElementById('assigneeSearch')?.focus();
                    }
                  }}
                  className="absolute right-0 top-0 bottom-0 px-3.5 flex items-center justify-center cursor-pointer text-[#50AB3D] focus:outline-none z-10"
                  aria-label="Toggle user dropdown"
                >
                  <svg
                    className={`w-4 h-4 flex-shrink-0 transition-transform ${isAssigneeDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                </button>
              </div>
              {isAssigneeDropdownOpen &&
                createPortal(
                  <div
                    ref={formAssigneeDropdownRef}
                    style={{
                      ...assigneeDropdownStyle,
                      overscrollBehavior: 'contain',
                    }}
                    data-portal-dropdown="true"
                    className="z-[99999] bg-[#FFFFFF] border border-border-default rounded-xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar py-1"
                    onScroll={handleAssigneeScroll}
                  >
                    {isAssigneesLoading ? (
                      <div className="p-3 text-center text-xs text-text-muted">
                        Loading users...
                      </div>
                    ) : assigneeList.length > 0 ? (
                      <>
                        {assigneeList.map((emp) => {
                          const isSelected =
                            String(assigneeId) === String(emp.id);
                          return (
                            <button
                              key={emp.id}
                              type="button"
                              className={`w-full flex items-center gap-3 text-left px-4 py-2.5 transition-colors ${
                                isSelected
                                  ? 'bg-[#EBF4EA]'
                                  : 'bg-[#FFFFFF] hover:bg-[#EBF4EA]'
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setAssigneeId(emp.id);
                                setAssigneeName(emp.name);
                                setAssigneeSearchQuery(emp.name);
                                setTimeout(
                                  () => setIsAssigneeDropdownOpen(false),
                                  0,
                                );
                              }}
                            >
                              <span
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-medium shrink-0 ${
                                  isSelected
                                    ? 'bg-[#50AB3D] text-white'
                                    : 'bg-[#EBF4EA] text-[#50AB3D]'
                                }`}
                              >
                                {(emp.name || '?').charAt(0).toUpperCase()}
                              </span>
                              <span className="min-w-0 flex flex-col">
                                <span
                                  className={`text-[14px] font-medium truncate ${
                                    isSelected
                                      ? 'text-[#50AB3D]'
                                      : 'text-[#1F2241]'
                                  }`}
                                >
                                  {emp.name}
                                </span>
                                <span className="text-[12px] text-[#868686] truncate">
                                  {emp.email}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                        {isFetchingNextAssigneePage && (
                          <div className="p-2 text-center text-xs text-text-muted">
                            Loading more...
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-3 text-center text-xs text-text-muted">
                        No users found
                      </div>
                    )}
                  </div>,
                  document.body,
                )}
            </div>
          </div>
        </div>
        {/* <div className="flex items-center gap-2 text-text-primary">
          <input
            type="checkbox"
            checked
            readOnly
            className="w-4 h-4 accent-primary rounded"
          />
          <span className="text-[14px] leading-[22px] font-semibold text-[#50AB3D] font-sf-pro-display">
            Add Source Information
          </span>
        </div> */}

        {/* <div className="bg-[#F5F5F5] rounded-[12px] p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="sourceType"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                Source Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="sourceType"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                placeholder="e.g. Website, Referral, Ads"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>

            <div className="flex items-center lg:pt-7">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPrimarySource}
                  onChange={(e) => setIsPrimarySource(e.target.checked)}
                  disabled={isPending}
                  className="w-4 h-4 accent-primary rounded"
                />
                <span className="text-sm font-medium text-text-primary">
                  Is Primary Source?
                </span>
              </label>
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="campaignId"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                Campaign ID
              </label>
              <input
                type="text"
                id="campaignId"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
                placeholder="Enter Campaign ID"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="campaignName"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                Campaign Name
              </label>
              <input
                type="text"
                id="campaignName"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter Campaign Name"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="utmSource"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                UTM Source
              </label>
              <input
                type="text"
                id="utmSource"
                value={utmSource}
                onChange={(e) => setUtmSource(e.target.value)}
                placeholder="e.g. Google"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="utmMedium"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                UTM Medium
              </label>
              <input
                type="text"
                id="utmMedium"
                value={utmMedium}
                onChange={(e) => setUtmMedium(e.target.value)}
                placeholder="e.g. cpc"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>

            <div className="flex flex-col gap-2 lg:col-span-2">
              <label
                htmlFor="utmCampaign"
                className="text-xs font-semibold text-text-primary tracking-wider"
              >
                UTM Campaign
              </label>
              <input
                type="text"
                id="utmCampaign"
                value={utmCampaign}
                onChange={(e) => setUtmCampaign(e.target.value)}
                placeholder="e.g. spring_sale"
                className="w-full h-11 px-4 rounded-xl bg-layer-1-page border border-border-default text-text-primary text-sm font-medium placeholder-text-muted focus:outline-none transition-colors focus:border-primary"
                disabled={isPending}
              />
            </div>
          </div>
        </div> */}

        {customFieldsData &&
          (customFieldsData?.results || customFieldsData).length > 0 && (
            <div className="flex flex-col gap-3">
              <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                Custom Fields
              </h4>
              <div className="bg-[#F5F5F5] rounded-[12px] p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[...(customFieldsData?.results || customFieldsData)]
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((field) => (
                      <div key={field.id} className="flex flex-col gap-2">
                        <label className="text-[14px] font-medium text-text-secondary flex items-center justify-between">
                          <span>
                            {field.name}{' '}
                            {field.is_required && (
                              <span className="text-red-500">*</span>
                            )}
                          </span>
                          {errors[`cf_${field.slug}`] && (
                            <span className="text-red-500 text-xs">
                              Required
                            </span>
                          )}
                        </label>
                        {field.field_type === 'select' ? (
                          <CustomSelect
                            value={customFieldsValues[field.slug] || ''}
                            onChange={(val) => {
                              setCustomFieldsValues({
                                ...customFieldsValues,
                                [field.slug]: val,
                              });
                              if (errors[`cf_${field.slug}`])
                                setErrors({
                                  ...errors,
                                  [`cf_${field.slug}`]: null,
                                });
                            }}
                            disabled={isPending}
                            error={Boolean(errors[`cf_${field.slug}`])}
                            className="w-full h-11"
                            textSizeClass="text-[14px] font-medium"
                            placeholder={`Select ${field.name}`}
                            options={(Array.isArray(field.options)
                              ? field.options
                              : typeof field.options === 'string'
                                ? field.options.split(',')
                                : []
                            ).map((opt) => ({
                              value: typeof opt === 'string' ? opt.trim() : opt,
                              label: typeof opt === 'string' ? opt.trim() : opt,
                            }))}
                          />
                        ) : field.field_type === 'boolean' ? (
                          <div className="flex items-center gap-6 h-11">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`cf_${field.slug}`}
                                checked={
                                  customFieldsValues[field.slug] === true
                                }
                                onChange={() => {
                                  setCustomFieldsValues({
                                    ...customFieldsValues,
                                    [field.slug]: true,
                                  });
                                  if (errors[`cf_${field.slug}`])
                                    setErrors({
                                      ...errors,
                                      [`cf_${field.slug}`]: null,
                                    });
                                }}
                                disabled={isPending}
                                className="w-4 h-4 accent-[#50AB3D]"
                              />
                              <span className="text-[14px] font-medium text-[#1F2241]">
                                True
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`cf_${field.slug}`}
                                checked={
                                  customFieldsValues[field.slug] === false
                                }
                                onChange={() => {
                                  setCustomFieldsValues({
                                    ...customFieldsValues,
                                    [field.slug]: false,
                                  });
                                  if (errors[`cf_${field.slug}`])
                                    setErrors({
                                      ...errors,
                                      [`cf_${field.slug}`]: null,
                                    });
                                }}
                                disabled={isPending}
                                className="w-4 h-4 accent-[#50AB3D]"
                              />
                              <span className="text-[14px] font-medium text-[#1F2241]">
                                False
                              </span>
                            </label>
                          </div>
                        ) : field.field_type === 'date' ? (
                          <DateTimePicker
                            selected={
                              customFieldsValues[field.slug]
                                ? new Date(customFieldsValues[field.slug])
                                : null
                            }
                            onChange={(date) => {
                              const formattedDate = date
                                ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                                : '';
                              setCustomFieldsValues({
                                ...customFieldsValues,
                                [field.slug]: formattedDate,
                              });
                              if (errors[`cf_${field.slug}`])
                                setErrors({
                                  ...errors,
                                  [`cf_${field.slug}`]: null,
                                });
                            }}
                            mode="date"
                            allowPastDates={true}
                            allowFutureDates={true}
                            dateFormat="dd-MM-yyyy"
                            placeholderText={`Select ${field.name}`}
                            disabled={isPending}
                            className={`w-full h-11 px-4 rounded-xl bg-[#FFFFFF] border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors[`cf_${field.slug}`] ? 'border-red-500' : 'border-border-default focus:border-[#50AB3D]'}`}
                          />
                        ) : (
                          <input
                            type={
                              field.field_type === 'number' ? 'number' : 'text'
                            }
                            value={customFieldsValues[field.slug] || ''}
                            onChange={(e) => {
                              setCustomFieldsValues({
                                ...customFieldsValues,
                                [field.slug]: e.target.value,
                              });
                              if (errors[`cf_${field.slug}`])
                                setErrors({
                                  ...errors,
                                  [`cf_${field.slug}`]: null,
                                });
                            }}
                            placeholder={`Enter ${field.name}`}
                            disabled={isPending}
                            className={`w-full h-11 px-4 rounded-xl bg-[#FFFFFF] border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${errors[`cf_${field.slug}`] ? 'border-red-500' : 'border-border-default focus:border-[#50AB3D]'}`}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
      </div>

      <div className="flex items-center justify-end px-6 py-5 border-t border-[#EBEBEB] shrink-0 gap-3 bg-white">
        <Button
          type="submit"
          disabled={isPending || handleCreateTagMutation.isPending}
          variant="primary"
          className="h-[44px] px-5 text-[15px] font-medium rounded-[8px] gap-2"
        >
          {handleCreateTagMutation.isPending ? (
            'Creating tag...'
          ) : isPending ? (
            'Saving...'
          ) : (
            <>
              {initialContact
                ? `Update ${entityLabel}`
                : `Create ${entityLabel}`}
              {!initialContact && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-[4px] border border-white text-[12px] leading-none">
                  +
                </span>
              )}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
